import { useCallback, useEffect, useRef, useState } from "react";
import { TScreeningEvent } from "../models";
import { getScreeningEvents } from "../network";

export const useScreeningEvents = (verificationRequestId?: string): [
  TScreeningEvent[] | null,
  boolean,
  Error | null,
  () => Promise<void>,
] => {
  const [result, setResult] = useState<{
    requestId: string;
    events: TScreeningEvent[];
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const requestSequence = useRef(0);

  const request = useCallback(async (): Promise<void> => {
    const sequence = ++requestSequence.current;
    setIsLoading(true);
    setError(null);

    if (verificationRequestId) {
      try {
        const events = await getScreeningEvents(verificationRequestId);
        if (sequence === requestSequence.current) {
          setResult({ requestId: verificationRequestId, events });
        }
      } catch (caught) {
        if (sequence === requestSequence.current) {
          setError(caught instanceof Error ? caught : new Error("Unknown error"));
        }
      }
    } else {
      setError(new Error("Verification request ID is not provided"));
    }

    if (sequence === requestSequence.current) {
      setIsLoading(false);
    }
  }, [verificationRequestId]);

  useEffect(() => {
    if (verificationRequestId) {
      request();
    }
    return () => {
      requestSequence.current += 1;
    };
  }, [request, verificationRequestId]);

  return [
    result && result.requestId === verificationRequestId ? result.events : null,
    isLoading,
    error,
    request,
  ];
};
